import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
  type ComboboxOption,
} from '@floraltech/ui';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@shell/AuthContext';
import { useUO } from '@shell/UOContext';
import { useUrlState } from '@lib/useUrlState';
import { AiutoCampo } from '../components/AiutoCampo';
import { fmtDate, fmtEuro } from '../lib/formato';

/**
 * Creazione di un contratto a canone, in ENTRAMBE le direzioni (Task 7,
 * BL-114): un contratto passivo (affitto, utenza, canone ricorrente...) o un
 * contratto attivo (un canone che fatturiamo noi a un cliente). La scelta
 * "Chi paga?" sta in testa al form, in URL (`tipo_rapporto`), e guida una
 * cascata di campi: chi si cerca, con quale natura si filtra il settore, se
 * la ritenuta e' in evidenza o sotto "Altre voci", se compare il campo
 * "Anticipato/posticipato" (mai sugli attivi, B4 referto dali) o il campo
 * "Il primo canone che fatturiamo noi" (mai sui passivi).
 *
 * Un solo form, non due percorsi: raccomandazione del referto dali
 * 2026-08-09 ("il form ha 1421 righe e ~20 fix gia' fatti; duplicare =
 * garantire divergenza").
 *
 * L'ANTEPRIMA E' BLINDATA sulla firma del payload (pattern FattureList.tsx:119-129,
 * referto dali I1): nessun onChange invalida a mano. Si calcola
 * `firmaCorrente = JSON.stringify(corpo())` a ogni render e la si confronta con
 * `firmaCalcolata`, la firma salvata al momento dell'ultimo calcolo riuscito. Se
 * un solo campo cambia dopo il calcolo (incluso `direzione`, quando si cambia
 * "Chi paga?"), `anteprimaValida` torna false e il pulsante di conferma si
 * spegne da solo: non c'e' un elenco di campi da dimenticare di invalidare.
 *
 * Niente aritmetica lato client sugli importi (quantita'/prezzo/aliquota
 * viaggiano come stringhe verso il backend): i numeri dell'anteprima sono
 * SEMPRE quelli calcolati dal server, mai ricalcolati qui. La sola
 * trasformazione client-side sui numeri e' la normalizzazione virgola->punto
 * (referto dali N4): non e' aritmetica, e' formato.
 *
 * Fix round 1 (2026-08-03, review tecnica + dali su "form costruito"): tipo,
 * periodicita', unita' di misura e natura IVA tengono SEPARATI il testo
 * digitato dal codice risolto (N2/N11) - lo stesso pattern gia' usato per
 * Fornitore e Settore, mai il valore inviato al backend prima di una
 * selezione valida.
 */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

interface Soggetto {
  id: string;
  ragione_sociale: string;
  partita_iva: string | null;
}

interface VoceLookup {
  codice: string;
  descrizione: string;
}

interface AliquotaIva {
  codice: string;
  descrizione: string;
  percentuale: number;
}

interface Centro {
  id: string;
  codice: string;
  nome: string;
  /** Un centro che compare come parent_id di un altro nella stessa risposta
   * e' un contenitore: il backend lo rifiuta (BL-113, "foglia via NOT
   * EXISTS"). Serve per filtrarli client-side, N8 fix round 2. */
  parent_id: string | null;
}

interface RigaForm {
  descrizione: string;
  quantita: string;
  /** Codice risolto (quello che parte nel payload). */
  unita_misura: string;
  /** Testo digitato/mostrato nel Combobox: puo' non corrispondere a nessun
   * codice finche' non si seleziona una voce (N2). */
  unitaMisuraInput: string;
  prezzo_unitario: string;
  aliquota_iva_percentuale: string;
  /** True solo se l'utente ha davvero toccato l'aliquota (a mano, o di
   * riflesso scegliendo una natura IVA): distingue un valore ancora al
   * default appena arrivato dalla lookup da uno scelto dalla persona (C2,
   * referto dali - "zero hardcoding" sull'aliquota di riga). */
  aliquotaIvaToccata: boolean;
  natura_iva_codice: string;
  naturaIvaInput: string;
}

interface PeriodoPrevisto {
  periodo_inizio: string;
  periodo_fine: string;
  data_scadenza: string;
  imponibile: string;
  iva: string;
  totale: string;
  ritenuta: string;
  netto_a_pagare: string;
}

interface ErroreCampoForm {
  id: string;
  campo: string;
  messaggio: string;
}

/** Riga vuota con l'aliquota IVA al default corrente della lookup aliquote
 * (C2): niente '22' cablato nel sorgente, l'unico input e' cio' che la
 * classificazione 'aliquote_iva' del tenant restituisce. */
const rigaVuota = (aliquotaDefault: string): RigaForm => ({
  descrizione: '',
  quantita: '1',
  unita_misura: '',
  unitaMisuraInput: '',
  prezzo_unitario: '',
  aliquota_iva_percentuale: aliquotaDefault,
  aliquotaIvaToccata: false,
  natura_iva_codice: '',
  naturaIvaInput: '',
});

/** Virgola italiana -> punto per i campi Decimal (referto dali N4/N5 fix
 * round 2): il backend accetta solo il punto. Non basta sostituire la
 * virgola: "1.000,00" (mille euro, separatore delle migliaia) diventava
 * "1.000.00" e falliva con un 422. Regola: se ci sono ENTRAMBI punto e
 * virgola, il punto e' il separatore delle migliaia (si toglie) e la
 * virgola e' il decimale; se c'e' SOLO la virgola, diventa il punto; se c'e'
 * solo il punto (o niente), il testo e' gia' nel formato che serve - TRANNE
 * il caso delle migliaia all'italiana SENZA virgola (review controllore
 * finale, Important I5): "1.000" letto cosi' com'e' vale 1 euro, non mille.
 * Si riconosce dalla FORMA, non dal valore: solo punti, e OGNI gruppo dopo
 * il primo ha ESATTAMENTE tre cifre ("1.000", "1.000.500"). Un gruppo
 * diverso da tre ("10.50") e' un punto decimale vero e resta invariato: non
 * c'e' modo di distinguere "10.500" (diecimilacinquecento) da un vero
 * decimale sulla sola forma, ma un gruppo da due o quattro cifre non lascia
 * dubbi. Non e' aritmetica: e' un cambio di formato sulla stringa, mai un
 * calcolo. */
const SOLO_MIGLIAIA_ALL_ITALIANA = /^\d{1,3}(\.\d{3})+$/;

const normDecimale = (s: string): string => {
  const t = s.trim();
  if (t.includes('.') && t.includes(',')) return t.replace(/\./g, '').replace(',', '.');
  if (t.includes(',')) return t.replace(',', '.');
  if (SOLO_MIGLIAIA_ALL_ITALIANA.test(t)) return t.replace(/\./g, '');
  return t;
};

const eNumericoZero = (s: string): boolean => {
  const n = normDecimale(s);
  return n !== '' && !Number.isNaN(Number(n)) && Number(n) === 0;
};

/** Oggi in ISO "YYYY-MM-DD", dalle parti locali (getFullYear/getMonth/
 * getDate): confrontabile lessicograficamente con le date che arrivano dal
 * backend. MAI toISOString() (converte a UTC: in Italia, un istante dopo
 * mezzanotte locale e' ancora ieri in UTC) - stesso pattern di
 * ContrattoDetail.tsx:191-196. */
const oggiISO = (): string => {
  const d = new Date();
  const mese = String(d.getMonth() + 1).padStart(2, '0');
  const giorno = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mese}-${giorno}`;
};

/** Il giorno successivo a una data ISO "YYYY-MM-DD": un periodo si puo'
 * fatturare dal giorno DOPO la sua fine (stessa regola del backend,
 * servizio.genera_periodo/periodo_da_fatturare - qui solo per mostrarlo in
 * anteprima, MAI per decidere cosa la creazione scrivera' davvero). Data
 * composta dalle parti locali, MAI `new Date("YYYY-MM-DD")`: stessa classe
 * del bug di fuso gia' corretta su oggiISO/fmtDate. */
const giornoDopo = (iso: string): string => {
  const [anno, mese, giorno] = iso.split('-').map(Number);
  const d = new Date(anno, mese - 1, giorno);
  d.setDate(d.getDate() + 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const gg = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${gg}`;
};

/** Filtro client-side su una lookup gia' caricata per intero (poche decine
 * di voci): niente giro di rete a ogni carattere digitato. */
const filtraLookup = (lista: VoceLookup[], query: string): VoceLookup[] => {
  const q = query.trim().toLowerCase();
  if (!q) return lista;
  return lista.filter(
    (v) => v.codice.toLowerCase().includes(q) || v.descrizione.toLowerCase().includes(q),
  );
};

const lookupAOpzioni = (lista: VoceLookup[]): ComboboxOption[] =>
  lista.map((v) => ({ value: v.codice, label: v.descrizione, sublabel: v.codice }));

/** Opzioni per una lookup con testo/codice separati: se il testo a schermo E'
 * gia' la descrizione del codice risolto (stato "a riposo", subito dopo una
 * selezione o al riapertura via chevron/openOnFocus) mostra la lista INTERA
 * invece di filtrarla su se stessa - altrimenti riaprire un campo gia'
 * valorizzato mostrerebbe una sola voce invece di tutta la tendina. */
const opzioniPerLookup = (
  lista: VoceLookup[],
  input: string,
  codiceAttuale: string,
): ComboboxOption[] => {
  const aRiposo =
    codiceAttuale !== '' && lista.find((v) => v.codice === codiceAttuale)?.descrizione === input;
  return lookupAOpzioni(aRiposo ? lista : filtraLookup(lista, input));
};

const LabelConAiuto: React.FC<{
  htmlFor: string;
  campo: string;
  aiuto: string;
  children: React.ReactNode;
}> = ({ htmlFor, campo, aiuto, children }) => (
  <div className="flex items-center gap-1">
    <Label htmlFor={htmlFor}>{children}</Label>
    <AiutoCampo campo={campo} testo={aiuto} />
  </div>
);

/** Selettore a due stati in stile radiogroup (pattern AccountSettings.tsx
 * SecuritySelect): posticipato/anticipato, giorni/mesi del preavviso, e ora
 * "Chi paga?" (Task 7). Un radiogroup non ha un singolo controllo a cui
 * associare un `<label for>` (fix round 2, N7/9): passare `ariaLabel` quando
 * non c'e' un testo visibile dedicato, o `ariaLabelledBy` (l'id di una
 * `<Label>` visibile gia' a schermo) quando c'e' - il pattern ARIA corretto,
 * non un `htmlFor` su un `div` che non e' labelable. */
function Selettore2<T extends string>({
  id,
  value,
  opzioni,
  onChange,
  disabled,
  ariaLabel,
  ariaLabelledBy,
}: {
  id?: string;
  value: T;
  opzioni: { value: T; label: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}) {
  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className="inline-flex rounded-md border border-border"
    >
      {opzioni.map((opt, idx) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 text-xs disabled:opacity-50 focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
            idx === 0 ? 'rounded-l-md' : ''
          } ${idx === opzioni.length - 1 ? 'rounded-r-md' : ''} ${
            value === opt.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Blocco richiudibile (pattern locale, come Selettore2/LabelConAiuto: non
 * esiste un Collapsible in ui_core, referto coder 5.2). Usato per "Altre
 * voci" (ritenuta sugli attivi, referto dali cascata: "SRL non subisce
 * ritenuta sulle vendite: conferma contabile", quindi fuori vista di
 * default ma non tolto - resta un campo valido se serve). Definito FUORI dal
 * componente principale: un componente dichiarato dentro il body di
 * ContrattoNuovo verrebbe rimontato (stato perso) a ogni render. */
const BloccoRichiudibile: React.FC<{ titolo: string; children: React.ReactNode }> = ({
  titolo,
  children,
}) => {
  const [aperto, setAperto] = useState(false);
  return (
    <div className="md:col-span-2 border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setAperto((a) => !a)}
        aria-expanded={aperto}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded"
      >
        {aperto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {titolo}
      </button>
      {aperto && <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>}
    </div>
  );
};

/** FastAPI manda `detail` come stringa o come array di errori di validazione
 * (loc + msg): senza normalizzare si legge "[object Object]" (referto dali I2). */
const normalizzaErrore = (dati: unknown, status: number, ripiego: string): string => {
  const detail = (dati as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parti = detail.map((e) => {
      const errore = e as { loc?: unknown[]; msg?: string };
      const loc = Array.isArray(errore.loc)
        ? errore.loc.filter((x) => x !== 'body').join('.')
        : '';
      return loc ? `${loc}: ${errore.msg ?? ''}` : (errore.msg ?? JSON.stringify(errore));
    });
    if (parti.length) return parti.join('; ');
  }
  return ripiego || `Errore (${status})`;
};

const ContrattoNuovo: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermesso } = useAuth();
  const { currentUO, availableUOs } = useUO();
  const puoGestire = hasPermesso('contratti.gestione');

  // --- Chi paga? (Task 7) ----------------------------------------------------
  // Stato in URL, non in useState (referto dali RACCOMANDAZIONE): un F5 o un
  // link diretto deve riproporre la stessa scelta. Valore ammesso solo
  // 'cliente'/'fornitore': un parametro sporco o assente ripiega su
  // 'cliente', mai un terzo stato indefinito.
  const [tipoRapportoUrl, setTipoRapportoUrl] = useUrlState('tipo_rapporto', 'cliente');
  const tipoRapporto: 'cliente' | 'fornitore' = tipoRapportoUrl === 'fornitore' ? 'fornitore' : 'cliente';
  const direzione: 'attivo' | 'passivo' = tipoRapporto === 'fornitore' ? 'passivo' : 'attivo';
  const baseLista = location.pathname.startsWith('/vendite/contratti')
    ? '/vendite/contratti'
    : location.pathname.startsWith('/acquisti/contratti')
      ? '/acquisti/contratti'
      : tipoRapporto === 'cliente'
        ? '/vendite/contratti'
        : '/acquisti/contratti';
  const [avvisoAzzeramento, setAvvisoAzzeramento] = useState(false);

  // --- Il contratto -------------------------------------------------------
  const [controparte, setControparte] = useState<Soggetto | null>(null);
  const [controparteInput, setControparteInput] = useState('');
  const [controparti, setControparti] = useState<Soggetto[]>([]);
  const [erroreControparti, setErroreControparti] = useState<string | null>(null);
  const [cercandoControparti, setCercandoControparti] = useState(false);

  const [oggetto, setOggetto] = useState('');
  const [tipo, setTipo] = useState('');
  const [tipoInput, setTipoInput] = useState('');
  const [tipi, setTipi] = useState<VoceLookup[]>([]);
  const [codice, setCodice] = useState('');

  const [centroCostoId, setCentroCostoId] = useState('');
  const [centroCostoInput, setCentroCostoInput] = useState('');
  const [centri, setCentri] = useState<Centro[]>([]);

  const [unitaId, setUnitaId] = useState<string>(currentUO?.id ?? '');
  const [unitaIdInput, setUnitaIdInput] = useState<string>(currentUO?.nome ?? '');
  const [unitaIdToccato, setUnitaIdToccato] = useState(false);
  // currentUO puo' arrivare DOPO il primo render (AuthContext/UOContext si
  // popolano da una fetch async): senza questo effetto un mount anticipato
  // lascerebbe unitaId vuoto per sempre. Ripiega sul primo ramo disponibile
  // se currentUO non risolve mai (B2: niente opzione vuota nel select).
  useEffect(() => {
    if (unitaIdToccato) return;
    const uo = currentUO ?? availableUOs[0] ?? null;
    if (uo) {
      setUnitaId(uo.id);
      setUnitaIdInput(uo.nome);
    }
  }, [currentUO, availableUOs, unitaIdToccato]);

  const [registrato, setRegistrato] = useState(false);
  const [estremiRegistrazione, setEstremiRegistrazione] = useState('');

  const [aliquotaRitenuta, setAliquotaRitenuta] = useState('');
  const [tipoRitenuta, setTipoRitenuta] = useState('');

  // --- Durata e rinnovo -----------------------------------------------------
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [rinnovoAuto, setRinnovoAuto] = useState(false);
  // Preavviso e durata del rinnovo: nessuna chiave di configurazione tenant
  // li copre oggi (verificato su configurazione_tenant, 0032_contratti_
  // config_tenant.py: le uniche seminate sono le due dei giorni di incasso/
  // pagamento). Restano gli stessi default di prima; C2 chiede di
  // sostituirli SOLO se una fonte esiste gia' - qui non esiste, quindi non
  // si tocca (segnalato nel report del Task 7).
  const [preavvisoValore, setPreavvisoValore] = useState('60');
  const [preavvisoValoreToccato, setPreavvisoValoreToccato] = useState(false);
  const [preavvisoUnita, setPreavvisoUnita] = useState<'giorni' | 'mesi'>('giorni');
  const [preavvisoUnitaToccata, setPreavvisoUnitaToccata] = useState(false);
  const [rinnovoDurataMesi, setRinnovoDurataMesi] = useState('12');

  // Default per una locazione: art. 28 L. 392/1978 esprime il preavviso in
  // mesi, non in giorni. Segue il tipo finche' non si tocca l'UNITA' o il
  // VALORE a mano (N13: se il valore e' gia' stato digitato, scegliere il
  // tipo dopo non deve sovrascriverlo).
  useEffect(() => {
    if (!preavvisoUnitaToccata && !preavvisoValoreToccato && tipo === 'locazione') {
      setPreavvisoUnita('mesi');
      setPreavvisoValore('12');
    }
  }, [tipo, preavvisoUnitaToccata, preavvisoValoreToccato]);

  // --- Ogni quanto ----------------------------------------------------------
  const [periodicita, setPeriodicita] = useState('');
  const [periodicitaInput, setPeriodicitaInput] = useState('');
  const [periodicitaList, setPeriodicitaList] = useState<VoceLookup[]>([]);
  const [ancoraGiorno, setAncoraGiorno] = useState('1');
  const [ancoraToccata, setAncoraToccata] = useState(false);
  // C2 (referto dali): niente '0' cablato. Vuoto finche' la configurazione
  // tenant non risponde; il payload ricade su 0 SOLO se la richiesta fallisce
  // (utente senza core.configurazione, o chiave non ancora seminata), non
  // perche' '0' sia il default di prodotto.
  const [giorniScadenza, setGiorniScadenza] = useState('');
  const [giorniScadenzaToccato, setGiorniScadenzaToccato] = useState(false);
  const giorniScadenzaToccatoRef = useRef(false);
  useEffect(() => {
    giorniScadenzaToccatoRef.current = giorniScadenzaToccato;
  }, [giorniScadenzaToccato]);
  const [pagamentoAnticipato, setPagamentoAnticipato] = useState(false);

  // Il giorno di ancoraggio segue la decorrenza finche' non lo si tocca a
  // mano: copre l'affitto firmato il 15 ma ancorato al 1 senza chiedere due
  // date diverse.
  useEffect(() => {
    if (!ancoraToccata && dataInizio) {
      // Data composta dalle parti locali (split), MAI new Date(stringa
      // "YYYY-MM-DD"): quella sintassi ECMA-262 e' mezzanotte UTC, e a
      // offset negativi getDate() tornerebbe il giorno sbagliato - stessa
      // classe del bug gia' corretto su fmtDate/oggiISO (review controllore
      // finale, Important I6).
      const [anno, mese, giorno] = dataInizio.split('-').map(Number);
      setAncoraGiorno(String(new Date(anno, mese - 1, giorno).getDate()));
    }
  }, [dataInizio, ancoraToccata]);

  // Giorni di incasso/pagamento di default: dalla configurazione tenant
  // (contratti.giorni_incasso_default sull'attivo, contratti.giorni_
  // pagamento_default sul passivo - migration 0032), non un numero fisso nel
  // sorgente (C2). Se la richiesta fallisce (permesso core.configurazione
  // mancante, o rete) il campo resta vuoto: il payload cade su 0 solo li',
  // non e' un default mostrato all'utente. Non riscrive un valore gia'
  // toccato dalla persona (guardia sul ref, sorpasso della risposta tardiva).
  useEffect(() => {
    if (giorniScadenzaToccato) return;
    let annullato = false;
    const chiave =
      direzione === 'attivo' ? 'contratti.giorni_incasso_default' : 'contratti.giorni_pagamento_default';
    void (async () => {
      const res = await fetch(`/api/v1/configurazione/${chiave}`, { headers: authHeaders() });
      if (annullato || giorniScadenzaToccatoRef.current) return;
      if (res.ok) {
        const dati = await res.json();
        if (dati?.valore != null) setGiorniScadenza(String(dati.valore));
      }
    })();
    return () => {
      annullato = true;
    };
  }, [direzione, giorniScadenzaToccato]);

  // --- Solo sugli attivi: quando entra in fatturazione, come si conta -------
  const [dataPavimento, setDataPavimento] = useState('');
  const [dataPavimentoToccato, setDataPavimentoToccato] = useState(false);
  // Default "l'inizio del periodo corrente": qui si usa la decorrenza del
  // contratto finche' non si tocca a mano. Calcolare il vero inizio del
  // periodo in corso richiederebbe duplicare l'aritmetica di ricorrenza.py
  // lato client (esattamente cio' che il referto coder segnala come "il
  // germe delle divergenze", sezione 1.5): si preferisce un default onesto e
  // sempre corretto (la decorrenza) a un ricalcolo che potrebbe disallinearsi
  // dal motore - scostamento dichiarato nel report del Task 7.
  useEffect(() => {
    if (dataPavimentoToccato) return;
    if (dataInizio) setDataPavimento(dataInizio);
  }, [dataInizio, dataPavimentoToccato]);

  const [sorgenteQuantita, setSorgenteQuantita] = useState('');
  const [sorgenteQuantitaInput, setSorgenteQuantitaInput] = useState('');
  const [sorgenteQuantitaList, setSorgenteQuantitaList] = useState<VoceLookup[]>([]);
  useEffect(() => {
    if (sorgenteQuantitaInput || sorgenteQuantita) return;
    const fissa = sorgenteQuantitaList.find((v) => v.codice === 'fissa');
    if (fissa) {
      setSorgenteQuantita(fissa.codice);
      setSorgenteQuantitaInput(fissa.descrizione);
    }
  }, [sorgenteQuantitaList, sorgenteQuantitaInput, sorgenteQuantita]);

  // --- Voci a canone ----------------------------------------------------------
  const [aliquoteOpts, setAliquoteOpts] = useState<AliquotaIva[]>([]);
  // Default di riga: l'aliquota piu' alta fra quelle configurate (l'aliquota
  // ordinaria e' sempre la piu' alta nel sistema italiano) - MAI '22' scritto
  // nel sorgente (C2, referto dali). Vuoto finche' la lookup non risponde.
  const aliquotaDefault = aliquoteOpts.length
    ? String(Math.max(...aliquoteOpts.map((a) => a.percentuale)))
    : '';
  const [righe, setRighe] = useState<RigaForm[]>([rigaVuota('')]);
  const [natureIva, setNatureIva] = useState<VoceLookup[]>([]);
  const [unitaMisure, setUnitaMisure] = useState<VoceLookup[]>([]);

  // Applica il default appena la lookup aliquote risponde, SOLO alle righe
  // ancora vuote e mai toccate: una riga gia' compilata (o su cui l'utente e'
  // gia' intervenuto) non viene riscritta sotto le dita.
  useEffect(() => {
    if (!aliquotaDefault) return;
    setRighe((prev) =>
      prev.map((r) =>
        r.aliquotaIvaToccata || r.aliquota_iva_percentuale.trim() !== ''
          ? r
          : { ...r, aliquota_iva_percentuale: aliquotaDefault },
      ),
    );
  }, [aliquotaDefault]);

  // --- Anteprima ----------------------------------------------------------
  const [anteprima, setAnteprima] = useState<PeriodoPrevisto[] | null>(null);
  const [totalePrevisto, setTotalePrevisto] = useState<string | null>(null);
  const [nettoPrevisto, setNettoPrevisto] = useState<string | null>(null);
  const [firmaCalcolata, setFirmaCalcolata] = useState<string | null>(null);
  const [calcolando, setCalcolando] = useState(false);
  const [erroreAnteprima, setErroreAnteprima] = useState<string | null>(null);
  const [orizzonteAnno, setOrizzonteAnno] = useState<number | null>(null);

  const [salvando, setSalvando] = useState(false);
  const [erroreCreazione, setErroreCreazione] = useState<string | null>(null);
  const [mostraErroriForm, setMostraErroriForm] = useState(false);

  const [confermaUscitaAperta, setConfermaUscitaAperta] = useState(false);

  // --- Caricamento lookup, una volta sola ------------------------------------
  useEffect(() => {
    const carica = async (
      url: string,
      esito: (dati: VoceLookup[]) => void,
    ) => {
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) esito(await res.json());
    };
    void carica('/api/v1/contratti/periodicita', setPeriodicitaList);
    void carica('/api/v1/contratti/lookup/tipo_contratto', setTipi);
    void carica('/api/v1/contratti/lookup/natura_iva', setNatureIva);
    void carica('/api/v1/contratti/lookup/unita_misura', setUnitaMisure);
    void carica('/api/v1/contratti/lookup/sorgente_quantita', setSorgenteQuantitaList);

    void (async () => {
      // Pattern fetchAliquote di NuovoPreventivoVendita.tsx (C2, referto
      // dali): stessa lookup, stesso endpoint, per non avere due default
      // dell'aliquota ordinaria che potrebbero divergere.
      const res = await fetch('/api/v1/classificazioni/aliquote-iva', { headers: authHeaders() });
      if (res.ok) setAliquoteOpts(await res.json());
    })();

    void (async () => {
      // N8 fix round 2: l'endpoint gia' filtra natura e attivo lato server
      // (centri.py:94), niente piu' scaricare 200 righe e filtrare a mano.
      // natura e' obbligatoria su CentroOut (mai assente): nessun ramo di
      // fallback, un contratto va SEMPRE a un centro di costo o di ricavo
      // secondo la direzione (BL-113, BL-114). In piu' escludiamo i
      // contenitori: un centro che compare come parent_id di un altro in
      // questa stessa risposta ha figli, e il backend lo rifiuta ("foglia
      // via NOT EXISTS") - anticipare il 422.
      //
      // Ricaricata quando cambia `direzione` (natura ricavo/costo diversa):
      // la dipendenza sta nell'effetto sotto, non qui.
    })();

    void (async () => {
      const res = await fetch('/api/v1/contratti/orizzonte-apertura', {
        headers: authHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setOrizzonteAnno(d.ultimo_anno_partizionato ?? null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Centri per il Settore: natura dipende dalla direzione (ricavo sugli
  // attivi, costo sui passivi - NATURA_PER_DIREZIONE_CONTRATTO,
  // contratti.py:110). Ricaricata a ogni cambio di "Chi paga?".
  useEffect(() => {
    const natura = direzione === 'attivo' ? 'ricavo' : 'costo';
    void (async () => {
      const res = await fetch(
        `/api/v1/centri-costo/centri?natura=${natura}&attivo=true&limit=200`,
        { headers: authHeaders() },
      );
      if (res.ok) {
        const d = await res.json();
        const items: Centro[] = Array.isArray(d) ? d : (d.items ?? []);
        const idsConFigli = new Set(
          items.map((c) => c.parent_id).filter((id): id is string => Boolean(id)),
        );
        setCentri(items.filter((c) => !idsConFigli.has(c.id)));
      }
    })();
  }, [direzione]);

  // Ricerca controparte (cliente o fornitore secondo "Chi paga?"): debounce
  // 250ms + guardia anti-sorpasso (pattern CambiaIntestatarioDialog.tsx
  // 92-132, referto N5). Non riparte se una controparte e' gia' selezionata:
  // l'input mostra la sua ragione sociale, non c'e' niente da cercare finche'
  // non la si tocca.
  const controparteGenerazioneRef = useRef(0);
  useEffect(() => {
    if (controparte) return;
    const q = controparteInput.trim();
    if (!q) {
      setControparti([]);
      setErroreControparti(null);
      setCercandoControparti(false);
      return;
    }
    const timer = setTimeout(async () => {
      controparteGenerazioneRef.current += 1;
      const generazione = controparteGenerazioneRef.current;
      setCercandoControparti(true);
      setErroreControparti(null);
      try {
        const res = await fetch(
          `/api/v1/soggetti?tipo=${tipoRapporto}&q=${encodeURIComponent(q)}&limit=20`,
          { headers: authHeaders() },
        );
        if (controparteGenerazioneRef.current !== generazione) return;
        if (!res.ok) {
          setErroreControparti(
            `Ricerca non riuscita (${res.status}): permesso mancante o servizio non raggiungibile.`,
          );
          setControparti([]);
          return;
        }
        const data = await res.json();
        setControparti(Array.isArray(data) ? data : (data.items ?? []));
      } catch {
        if (controparteGenerazioneRef.current === generazione) {
          setErroreControparti('Ricerca non riuscita: errore di rete.');
          setControparti([]);
        }
      } finally {
        if (controparteGenerazioneRef.current === generazione) setCercandoControparti(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [controparteInput, controparte, tipoRapporto]);

  // Cambio di "Chi paga?": azzera SOLO controparte e settore (referto dali
  // RACCOMANDAZIONE), avviso in linea, nessun dialogo. Tutto il resto del
  // form (date, periodicita', voci...) resta com'era.
  const cambiaTipoRapporto = (nuovo: 'cliente' | 'fornitore') => {
    setControparte(null);
    setControparteInput('');
    setControparti([]);
    setErroreControparti(null);
    setCentroCostoId('');
    setCentroCostoInput('');
    setAvvisoAzzeramento(true);
    setTipoRapportoUrl(nuovo);
  };

  const setRigaCampo = (i: number, campo: keyof RigaForm, valore: string) => {
    setRighe((prev) => prev.map((r, idx) => (idx === i ? { ...r, [campo]: valore } : r)));
  };

  // --- Unita' di misura per riga: testo/codice separati (N2) -----------------
  const cambiaUnitaMisuraInput = (i: number, v: string) => {
    setRighe((prev) =>
      prev.map((r, idx) => {
        if (idx !== i) return r;
        const match = unitaMisure.find((u) => u.descrizione === v);
        return { ...r, unitaMisuraInput: v, unita_misura: match ? match.codice : '' };
      }),
    );
  };
  const selezionaUnitaMisura = (i: number, opt: ComboboxOption) => {
    setRighe((prev) =>
      prev.map((r, idx) =>
        idx === i ? { ...r, unita_misura: opt.value, unitaMisuraInput: opt.label } : r,
      ),
    );
  };

  // --- Natura IVA per riga: testo/codice separati (N2) ------------------------
  const cambiaNaturaIvaInput = (i: number, v: string) => {
    setRighe((prev) =>
      prev.map((r, idx) => {
        if (idx !== i) return r;
        const match = natureIva.find((n) => n.descrizione === v);
        return { ...r, naturaIvaInput: v, natura_iva_codice: match ? match.codice : '' };
      }),
    );
  };
  const selezionaNaturaRiga = (i: number, opt: ComboboxOption) => {
    // Controllo SDI 00401: natura presente esige aliquota zero. Impostarla
    // insieme alla selezione evita un 422 prevedibile al calcolo.
    setRighe((prev) =>
      prev.map((r, idx) =>
        idx === i
          ? {
              ...r,
              natura_iva_codice: opt.value,
              naturaIvaInput: opt.label,
              aliquota_iva_percentuale: '0',
              aliquotaIvaToccata: true,
            }
          : r,
      ),
    );
  };

  const cambiaAliquotaRiga = (i: number, valore: string) => {
    // Controllo SDI 00401 nell'altro verso: un'aliquota diversa da zero non
    // puo' convivere con una natura gia' scelta. Il confronto normalizza la
    // virgola PRIMA di decidere se e' zero (N4): "0,00" e' zero uguale a "0".
    const eZero = eNumericoZero(valore);
    setRighe((prev) =>
      prev.map((r, idx) =>
        idx === i
          ? {
              ...r,
              aliquota_iva_percentuale: valore,
              aliquotaIvaToccata: true,
              natura_iva_codice: eZero ? r.natura_iva_codice : '',
              naturaIvaInput: eZero ? r.naturaIvaInput : '',
            }
          : r,
      ),
    );
  };

  const aggiungiRiga = () => setRighe((prev) => [...prev, rigaVuota(aliquotaDefault)]);
  const rimuoviRiga = (i: number) => setRighe((prev) => prev.filter((_, idx) => idx !== i));

  // --- Payload e firma ------------------------------------------------------

  const corpo = () => ({
    soggetto_id: controparte?.id,
    direzione,
    tipo,
    oggetto,
    codice: codice.trim() || null,
    unita_id: unitaId || null,
    centro_costo_id: centroCostoId || null,
    data_inizio: dataInizio,
    data_fine: dataFine || null,
    rinnovo_auto: rinnovoAuto,
    preavviso_giorni:
      rinnovoAuto && preavvisoUnita === 'giorni' && preavvisoValore.trim()
        ? Number(normDecimale(preavvisoValore))
        : null,
    preavviso_mesi:
      rinnovoAuto && preavvisoUnita === 'mesi' && preavvisoValore.trim()
        ? Number(normDecimale(preavvisoValore))
        : null,
    registrato,
    estremi_registrazione: registrato && estremiRegistrazione.trim()
      ? estremiRegistrazione.trim()
      : null,
    aliquota_ritenuta: aliquotaRitenuta.trim() ? normDecimale(aliquotaRitenuta) : null,
    tipo_ritenuta: tipoRitenuta.trim() || null,
    ricorrenza: {
      periodicita,
      data_ancora: dataInizio,
      ancora_giorno: Number(ancoraGiorno) || 1,
      giorni_scadenza_pagamento: Number(giorniScadenza) || 0,
      rinnovo_durata_mesi: rinnovoAuto && rinnovoDurataMesi.trim() ? Number(rinnovoDurataMesi) : null,
      // Anticipato/posticipato non ha effetto sugli attivi (B4 referto dali:
      // il motore di generazione propone solo periodi CONCLUSI) e il campo
      // non si mostra: sempre posticipato nel payload, per non mandare al
      // backend una scelta che l'utente non ha mai visto.
      pagamento_anticipato: direzione === 'passivo' ? pagamentoAnticipato : false,
      // Solo sugli attivi: sui passivi resta 'fissa', il default dello
      // schema (RicorrenzaIn.sorgente_quantita_codice).
      sorgente_quantita_codice: direzione === 'attivo' && sorgenteQuantita ? sorgenteQuantita : 'fissa',
      data_pavimento: direzione === 'attivo' && dataPavimento ? dataPavimento : null,
    },
    righe: righe.map((r) => ({
      descrizione: r.descrizione,
      quantita: r.quantita.trim() ? normDecimale(r.quantita) : null,
      unita_misura: r.unita_misura.trim() || null,
      prezzo_unitario: normDecimale(r.prezzo_unitario),
      aliquota_iva_percentuale: normDecimale(r.aliquota_iva_percentuale),
      natura_iva_codice: r.natura_iva_codice.trim() || null,
    })),
  });

  // Ricalcolata a ogni render: e' la STESSA lettura che l'anteprima ha
  // fotografato al momento del calcolo. Se differisce (incluso un cambio di
  // "Chi paga?", che cambia `direzione`), i numeri a schermo non sono piu'
  // quelli che verrebbero creati.
  const firmaCorrente = JSON.stringify(corpo());
  const anteprimaValida = anteprima !== null && firmaCalcolata === firmaCorrente;

  // N10 fix round 2: un'aliquota zero senza natura non e' solo segnalata
  // sotto la riga, spegne anche Calcola col motivo - il backend la
  // rifiuterebbe comunque (RigaIn._coerenza_aliquota_natura, SDI 00400).
  const rigaHaAliquotaZeroSenzaNatura = (r: RigaForm) =>
    eNumericoZero(r.aliquota_iva_percentuale) && !r.natura_iva_codice;

  // Sporco (13): calcolato sui campi davvero toccati dall'utente, non su
  // valori che si auto-compilano (unitaId da currentUO, ancoraGiorno dalla
  // decorrenza, preavviso dal tipo locazione, dataPavimento dalla decorrenza,
  // giorniScadenza/aliquota di riga dalla configurazione/lookup) - per quelli
  // conta il flag "toccato", non il valore. `registrato` da solo gia' basta a
  // sporcare il form (e' un checkbox: acceso e' acceso); il ramo che
  // guardava anche `estremiRegistrazione` in AND con `registrato` era
  // percio' morto - irraggiungibile, perche' il primo OR aveva gia' deciso
  // (fix round 2).
  const sporco = Boolean(
    controparteInput.trim() ||
      controparte ||
      oggetto.trim() ||
      tipoInput.trim() ||
      codice.trim() ||
      centroCostoInput.trim() ||
      unitaIdToccato ||
      dataInizio ||
      dataFine ||
      rinnovoAuto ||
      preavvisoUnitaToccata ||
      preavvisoValoreToccato ||
      rinnovoDurataMesi !== '12' ||
      registrato ||
      aliquotaRitenuta.trim() ||
      tipoRitenuta.trim() ||
      periodicitaInput.trim() ||
      ancoraToccata ||
      giorniScadenzaToccato ||
      pagamentoAnticipato ||
      dataPavimentoToccato ||
      sorgenteQuantitaInput.trim() ||
      righe.some(
        (r) =>
          r.descrizione.trim() ||
          r.prezzo_unitario.trim() ||
          r.naturaIvaInput.trim() ||
          r.unitaMisuraInput.trim() ||
          (r.quantita.trim() && r.quantita !== '1') ||
          r.aliquotaIvaToccata,
      ),
  );

  const chiediUscita = () => {
    if (sporco) setConfermaUscitaAperta(true);
    else navigate(-1);
  };

  // --- Chiamate -------------------------------------------------------------

  const calcolaAnteprima = async () => {
    if (erroriForm.length > 0) {
      setMostraErroriForm(true);
      setErroreAnteprima("Compila i campi evidenziati prima di calcolare l'anteprima.");
      queueMicrotask(focusPrimoErrore);
      return;
    }
    setCalcolando(true);
    setErroreAnteprima(null);
    const firmaAlMomento = firmaCorrente;
    try {
      const res = await fetch('/api/v1/contratti/anteprima-periodi', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: firmaAlMomento,
      });
      const dati = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(normalizzaErrore(dati, res.status, `Calcolo non riuscito (${res.status})`));
      }
      setAnteprima(dati.periodi);
      setTotalePrevisto(dati.totale_previsto ?? null);
      setNettoPrevisto(dati.netto_a_pagare_previsto ?? null);
      setFirmaCalcolata(firmaAlMomento);
    } catch (err) {
      setErroreAnteprima(err instanceof Error ? err.message : 'Errore imprevisto');
      setAnteprima(null);
      setFirmaCalcolata(null);
    } finally {
      setCalcolando(false);
    }
  };

  // Un periodo sotto "Il primo canone che fatturiamo noi" non e' fatturabile,
  // MAI: e' la STESSA regola di salto del backend
  // (ricorrenza.periodo_da_fatturare, "if data_pavimento is not None and
  // fine < data_pavimento: continue" - ricorrenza.py:139-143), replicata qui
  // solo per non mostrare in anteprima (o promettere nell'esito) un canone
  // che il motore scarterebbe sempre. Confronto di STRINGHE ISO (mai new
  // Date, mai aritmetica di date): "YYYY-MM-DD" si confronta
  // lessicograficamente come una data, stesso pattern gia' in uso su questa
  // pagina (fatturabileDal <= oggi).
  const sottoIlPavimento = (periodoFine: string): boolean =>
    direzione === 'attivo' && Boolean(dataPavimento) && periodoFine < dataPavimento;

  const crea = async () => {
    if (erroriForm.length > 0) {
      setMostraErroriForm(true);
      setErroreCreazione('Compila i campi evidenziati prima di creare il contratto.');
      queueMicrotask(focusPrimoErrore);
      return;
    }
    if (!anteprimaValida) {
      setErroreCreazione(
        "L'anteprima non corrisponde piu' ai dati del form: ricalcolarla prima di confermare.",
      );
      return;
    }
    setSalvando(true);
    setErroreCreazione(null);
    try {
      const res = await fetch('/api/v1/contratti', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        // apri_periodi:true esplicito per ENTRAMBE le direzioni: dal Task 2
        // il backend apre periodi e scadenze anche sugli attivi
        // (apertura.apri_periodi, apertura.py:100-104).
        body: JSON.stringify({ ...corpo(), apri_periodi: true }),
      });
      const dati = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(normalizzaErrore(dati, res.status, `Creazione non riuscita (${res.status})`));
      }
      let esito: string;
      if (direzione === 'attivo') {
        // La data del primo canone fatturabile viene dall'ANTEPRIMA gia'
        // calcolata (il primo periodo e' il piu' vicino, concluso o
        // prossimo che sia), mai da un calcolo rifatto qui. Se il primo
        // periodo dell'anteprima e' sotto il pavimento (il motore lo
        // salterebbe sempre), la data giusta e' quella del primo periodo NON
        // saltato - stessa regola di ricorrenza.periodo_da_fatturare.
        const primoPeriodo = anteprima?.find((p) => !sottoIlPavimento(p.periodo_fine)) ?? null;
        esito = primoPeriodo
          ? `Contratto creato. Il primo canone si potra' fatturare dal ${fmtDate(giornoDopo(primoPeriodo.periodo_fine))}.`
          : 'Contratto creato.';
      } else {
        const scadenzeAperte = dati.scadenze_aperte;
        esito =
          typeof scadenzeAperte === 'number'
            ? `Contratto creato: aperte ${scadenzeAperte} scadenze per ${fmtEuro(dati.totale_scadenze_aperte ?? null)}.`
            : 'Contratto creato.';
      }
      // replace:true: senza, "Indietro" dal dettaglio riporterebbe al form di
      // creazione ormai vuoto invece che all'elenco di provenienza (referto
      // controllore, grave 2).
      navigate(`${baseLista}/${dati.id}`, { state: { esito, elencoPath: baseLista }, replace: true });
    } catch (err) {
      setErroreCreazione(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setSalvando(false);
    }
  };

  const mostraRitenuta = aliquotaRitenuta.trim() !== '';
  const oggiIso = oggiISO();
  const erroriForm: ErroreCampoForm[] = [];
  if (!controparte) erroriForm.push({ id: 'controparte-input', campo: tipoRapporto === 'cliente' ? 'cliente' : 'fornitore', messaggio: tipoRapporto === 'cliente' ? 'Seleziona il cliente del contratto.' : 'Seleziona il fornitore del contratto.' });
  if (!tipo) erroriForm.push({ id: 'tipo-input', campo: 'tipo contratto', messaggio: 'Seleziona il tipo contratto.' });
  if (!oggetto.trim()) erroriForm.push({ id: 'oggetto-input', campo: 'oggetto', messaggio: "Compila l'oggetto del contratto." });
  if (!centroCostoId) erroriForm.push({ id: 'settore-input', campo: 'settore', messaggio: 'Seleziona il settore di imputazione.' });
  if (availableUOs.length > 1 && !unitaId) erroriForm.push({ id: 'ramo-input', campo: 'ramo', messaggio: 'Seleziona il ramo operativo del contratto.' });
  if (!dataInizio) erroriForm.push({ id: 'data-inizio-input', campo: 'decorrenza', messaggio: 'Compila la decorrenza del contratto.' });
  if (!periodicita) erroriForm.push({ id: 'periodicita-input', campo: "periodicita'", messaggio: "Seleziona la periodicita' del contratto." });
  if (rinnovoAuto && !dataFine) erroriForm.push({ id: 'data-fine-input', campo: 'scadenza', messaggio: 'Il rinnovo automatico richiede una scadenza.' });
  if (rinnovoAuto && !preavvisoValore.trim()) erroriForm.push({ id: 'preavviso-valore-input', campo: 'preavviso', messaggio: 'Compila il preavviso del rinnovo automatico.' });
  if (rinnovoAuto && !rinnovoDurataMesi.trim()) erroriForm.push({ id: 'rinnovo-durata-input', campo: 'durata rinnovo', messaggio: 'Compila la durata del rinnovo automatico.' });
  if (registrato && !estremiRegistrazione.trim()) erroriForm.push({ id: 'estremi-registrazione-input', campo: 'estremi registrazione', messaggio: 'Compila gli estremi di registrazione.' });
  if (mostraRitenuta && !tipoRitenuta.trim()) erroriForm.push({ id: 'tipo-ritenuta-input', campo: 'tipo ritenuta', messaggio: 'Compila il tipo ritenuta.' });
  if (direzione === 'attivo' && Boolean(dataInizio) && dataInizio < oggiIso && !dataPavimento) erroriForm.push({ id: 'data-pavimento-input', campo: 'primo canone da noi', messaggio: "Per un contratto attivo gia' in essere indica da quando fatturiamo noi o ricostruisci lo storico." });
  righe.forEach((r, i) => {
    if (!r.descrizione.trim()) erroriForm.push({ id: `descrizione-riga-${i}`, campo: `descrizione riga ${i + 1}`, messaggio: `Compila la descrizione della riga ${i + 1}.` });
    if (!r.unita_misura.trim()) erroriForm.push({ id: `unita-misura-riga-${i}`, campo: `unita' riga ${i + 1}`, messaggio: `Seleziona l'unita' di misura della riga ${i + 1}.` });
    if (!r.prezzo_unitario.trim()) erroriForm.push({ id: `prezzo-riga-${i}`, campo: `prezzo riga ${i + 1}`, messaggio: `Compila il prezzo della riga ${i + 1}.` });
    if (!r.aliquota_iva_percentuale.trim()) erroriForm.push({ id: `aliquota-riga-${i}`, campo: `aliquota IVA riga ${i + 1}`, messaggio: `Compila l'aliquota IVA della riga ${i + 1}.` });
    if (rigaHaAliquotaZeroSenzaNatura(r)) erroriForm.push({ id: `natura-iva-riga-${i}`, campo: `natura IVA riga ${i + 1}`, messaggio: `Con aliquota zero devi indicare la natura IVA della riga ${i + 1}.` });
  });
  const erroriPerCampo = new Map(erroriForm.map((errore) => [errore.id, errore]));
  const erroreCampo = (id: string): ErroreCampoForm | null => (mostraErroriForm ? (erroriPerCampo.get(id) ?? null) : null);
  const bordoErrore = (id: string): string => (erroreCampo(id) ? 'rounded-md border border-destructive/50 p-2' : '');
  const classeErroreInput = (id: string): string =>
    erroreCampo(id) ? 'border-destructive focus-visible:ring-destructive' : '';
  const focusPrimoErrore = () => {
    const primo = erroriForm[0];
    if (!primo) return;
    const nodo = document.getElementById(primo.id);
    if (nodo instanceof HTMLElement) nodo.focus();
  };

  const puoCalcolare = erroriForm.length === 0;

  // N6: cosa manca, per il testo accanto al pulsante.
  const mancantiCalcolo = erroriForm.map((errore) => errore.campo);
  const motivoCalcolaVisibile = !puoCalcolare && !calcolando && mancantiCalcolo.length > 0;

  // I3: avviso ATTIVO se l'ultima scadenza dell'anteprima supera il
  // 31/12 dell'orizzonte partizionato - non basta dirlo in piccolo accanto
  // alla tabella, la creazione la rifiuterebbe (apertura.py) e l'avviso deve
  // stare dove si guarda per confermare.
  const scadenzaOltreOrizzonte =
    anteprima != null &&
    orizzonteAnno != null &&
    anteprima.some((p) => {
      // Anno dalla parte locale (split), MAI new Date(stringa ISO): stessa
      // classe del bug di fuso gia' corretta su fmtDate/oggiISO (review
      // controllore finale, Important I6).
      const [anno] = p.data_scadenza.split('-').map(Number);
      return anno > orizzonteAnno;
    });

  // Sugli attivi: quanti canoni dell'anteprima risulterebbero gia'
  // fatturabili appena creato il contratto (giorno dopo la fine del periodo
  // <= oggi, ed escludendo cio' che il pavimento salterebbe). Confronto
  // lessicografico su stringhe ISO, stesso pattern gia' usato in
  // ContrattoDetail.tsx:882 (p.scadenza.data < oggiISO()).
  const oggi = oggiISO();
  const fatturabiliSubito =
    direzione === 'attivo' && anteprima
      ? anteprima.filter((p) => !sottoIlPavimento(p.periodo_fine) && giornoDopo(p.periodo_fine) <= oggi).length
      : 0;

  const motivoCreaSpenta = (): string | null => {
    if (salvando) return null;
    if (anteprima === null) return "Calcola prima l'anteprima.";
    if (!anteprimaValida) return "I dati sono cambiati dopo il calcolo: ricalcola l'anteprima.";
    return null;
  };

  if (!puoGestire) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          Indietro
        </Button>
        <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          Non hai il permesso di creare contratti (serve contratti.gestione).
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={chiediUscita}>
          <ArrowLeft className="h-4 w-4" />
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold">
          {tipoRapporto === 'cliente' ? 'Nuovo contratto a un cliente' : 'Nuova spesa ricorrente'}
        </h1>
      </div>

      {mostraErroriForm && erroriForm.length > 0 && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <div>
            <p className="font-medium">Compila i campi evidenziati prima di proseguire.</p>
            <p>{erroriForm.slice(0, 5).map((errore) => errore.campo).join(', ')}{erroriForm.length > 5 ? ', ...' : ''}.</p>
          </div>
        </div>
      )}

      {/* --- Chi paga? (Task 7) --- */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card p-3">
        <div>
          <span id="chi-paga-label" className="text-sm font-medium">
            Chi paga?
          </span>
          <div className="mt-1">
            <Selettore2
              ariaLabelledBy="chi-paga-label"
              value={tipoRapporto}
              opzioni={[
                { value: 'cliente', label: 'Ci paga un cliente' },
                { value: 'fornitore', label: 'Paghiamo un fornitore' },
              ]}
              onChange={cambiaTipoRapporto}
            />
          </div>
        </div>
        {avvisoAzzeramento && (
          <p className="text-xs text-muted-foreground">
            Cambiando "Chi paga?" si sono azzerati cliente/fornitore e settore.
          </p>
        )}
      </div>

      {/* --- Il contratto --- */}
      <Card>
        <CardHeader>
          <CardTitle>Il contratto</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className={`md:col-span-2 ${bordoErrore('controparte-input')}`}>
            <Label htmlFor="controparte-input">
              {tipoRapporto === 'cliente' ? 'Cliente' : 'Fornitore'}
            </Label>
            <Combobox
              id="controparte-input"
              autoFocus
              loading={cercandoControparti}
              ariaInvalid={Boolean(erroreCampo('controparte-input'))}
              ariaDescribedBy={erroreCampo('controparte-input') ? 'controparte-input-errore' : undefined}
              inputClassName={classeErroreInput('controparte-input')}
              value={controparteInput}
              onInputChange={(v) => {
                setControparteInput(v);
                setAvvisoAzzeramento(false);
                if (controparte) setControparte(null);
              }}
              onSelect={(o) => {
                const trovato = controparti.find((c) => c.id === o.value) ?? null;
                setControparte(trovato);
                setControparteInput(o.label);
                setAvvisoAzzeramento(false);
              }}
              options={controparti.map((c) => ({
                value: c.id,
                label: c.ragione_sociale,
                sublabel: c.partita_iva ?? undefined,
              }))}
              placeholder={
                tipoRapporto === 'cliente'
                  ? 'Cerca cliente per ragione sociale, codice fiscale o partita IVA...'
                  : 'Cerca fornitore per ragione sociale o codice fiscale...'
              }
            />
            {erroreCampo('controparte-input') && (
              <p id="controparte-input-errore" role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('controparte-input')?.messaggio}</p>
            )}
            {erroreControparti && (
              <p role="alert" className="mt-1 text-xs text-destructive">
                {erroreControparti}
              </p>
            )}
          </div>

          <div className={bordoErrore('tipo-input')}>
            <Label htmlFor="tipo-input">Tipo contratto</Label>
            <Combobox
              id="tipo-input"
              openOnFocus
              ariaInvalid={Boolean(erroreCampo('tipo-input'))}
              ariaDescribedBy={erroreCampo('tipo-input') ? 'tipo-input-errore' : undefined}
              inputClassName={classeErroreInput('tipo-input')}
              value={tipoInput}
              onInputChange={(v) => {
                setTipoInput(v);
                const match = tipi.find((t) => t.descrizione === v);
                setTipo(match ? match.codice : '');
              }}
              onSelect={(o) => {
                setTipo(o.value);
                setTipoInput(o.label);
              }}
              options={opzioniPerLookup(tipi, tipoInput, tipo)}
              placeholder="Cerca il tipo..."
            />
            {erroreCampo('tipo-input') && (
              <p id="tipo-input-errore" role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('tipo-input')?.messaggio}</p>
            )}
          </div>
          <div>
            <Label htmlFor="codice-input">Codice (facoltativo)</Label>
            <Input
              id="codice-input"
              value={codice}
              onChange={(e) => setCodice(e.target.value)}
              placeholder="Es. CTR-2026/0001"
            />
          </div>

          <div className={`md:col-span-2 ${bordoErrore('oggetto-input')}`}>
            <Label htmlFor="oggetto-input">Oggetto</Label>
            <Input
              id="oggetto-input"
              value={oggetto}
              onChange={(e) => setOggetto(e.target.value)}
              placeholder={
                direzione === 'attivo'
                  ? 'Es. Canone mensile di manutenzione sito web'
                  : 'Es. Affitto capannone Via Roma 12'
              }
              aria-invalid={Boolean(erroreCampo('oggetto-input'))}
              aria-describedby={erroreCampo('oggetto-input') ? 'oggetto-input-errore' : undefined}
              className={classeErroreInput('oggetto-input')}
            />
            {erroreCampo('oggetto-input') && (
              <p id="oggetto-input-errore" role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('oggetto-input')?.messaggio}</p>
            )}
          </div>

          <div className={bordoErrore('settore-input')}>
            <LabelConAiuto
              htmlFor="settore-input"
              campo="settore"
              aiuto={
                direzione === 'attivo'
                  ? "Su quale attivita' entra questo ricavo. Mostra i centri attivi; se il centro scelto raggruppa altri centri, la creazione lo segnalera'."
                  : "Mostra i centri di costo attivi; se il centro scelto raggruppa altri centri, la creazione lo segnalera'."
              }
            >
              Settore
            </LabelConAiuto>
            <Combobox
              id="settore-input"
              ariaInvalid={Boolean(erroreCampo('settore-input'))}
              ariaDescribedBy={erroreCampo('settore-input') ? 'settore-input-errore' : undefined}
              inputClassName={classeErroreInput('settore-input')}
              value={centroCostoInput}
              onInputChange={(v) => {
                setCentroCostoInput(v);
                setAvvisoAzzeramento(false);
                // N2/N4 fix round 2: come tipo/periodicita'/natura/U.M., un
                // testo che non corrisponde ESATTAMENTE all'etichetta del
                // centro gia' selezionato azzera il codice - altrimenti si
                // poteva digitare sopra un'etichetta valida e inviare ancora
                // l'id vecchio (il codice non veniva mai letto dal testo).
                const etichettaAttuale = centri.find((c) => c.id === centroCostoId);
                const etichettaTesto = etichettaAttuale
                  ? `${etichettaAttuale.codice} - ${etichettaAttuale.nome}`
                  : '';
                if (v !== etichettaTesto) setCentroCostoId('');
              }}
              onSelect={(o) => {
                setCentroCostoId(o.value);
                setCentroCostoInput(o.label);
                setAvvisoAzzeramento(false);
              }}
              options={centri
                .filter(
                  (c) =>
                    !centroCostoInput.trim() ||
                    c.codice.toLowerCase().includes(centroCostoInput.toLowerCase()) ||
                    c.nome.toLowerCase().includes(centroCostoInput.toLowerCase()),
                )
                .map((c) => ({ value: c.id, label: `${c.codice} - ${c.nome}` }))}
              placeholder="Cerca il settore..."
            />
            {erroreCampo('settore-input') && (
              <p id="settore-input-errore" role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('settore-input')?.messaggio}</p>
            )}
          </div>
          {availableUOs.length > 1 && (
            <div className={bordoErrore('ramo-input')}>
              <Label htmlFor="ramo-input">Ramo</Label>
              <Combobox
                id="ramo-input"
                openOnFocus
                ariaInvalid={Boolean(erroreCampo('ramo-input'))}
                ariaDescribedBy={erroreCampo('ramo-input') ? 'ramo-input-errore' : undefined}
                inputClassName={classeErroreInput('ramo-input')}
                value={unitaIdInput}
                onInputChange={(v) => {
                  setUnitaIdInput(v);
                  setUnitaIdToccato(true);
                  const match = availableUOs.find((u) => u.nome === v);
                  setUnitaId(match ? match.id : '');
                }}
                onSelect={(o) => {
                  setUnitaId(o.value);
                  setUnitaIdInput(o.label);
                  setUnitaIdToccato(true);
                }}
                options={availableUOs
                  .filter(
                    (u) => !unitaIdInput.trim() || u.nome.toLowerCase().includes(unitaIdInput.toLowerCase()),
                  )
                  .map((u) => ({ value: u.id, label: u.nome }))}
                placeholder="Cerca il ramo..."
              />
              {erroreCampo('ramo-input') && (
                <p id="ramo-input-errore" role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('ramo-input')?.messaggio}</p>
              )}
            </div>
          )}

          <div className="md:col-span-2 flex items-center gap-2 pt-2">
            <input
              id="registrato-check"
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={registrato}
              onChange={(e) => setRegistrato(e.target.checked)}
            />
            <label htmlFor="registrato-check" className="text-sm">
              Contratto registrato all'Agenzia delle Entrate
            </label>
            <AiutoCampo
              campo="registrato"
              testo="Se registrato, alla proroga tacita compare il promemoria dell'imposta di registro (art. 17 DPR 131/1986, 30 giorni per il modello RLI)."
            />
          </div>
          {registrato && (
            <div className={`md:col-span-2 ${bordoErrore('estremi-registrazione-input')}`}>
              <Label htmlFor="estremi-registrazione-input">Estremi di registrazione</Label>
              <Input
                id="estremi-registrazione-input"
                value={estremiRegistrazione}
                onChange={(e) => setEstremiRegistrazione(e.target.value)}
                placeholder="Es. Serie 3T n. 12345, Ufficio di Cosenza"
                aria-invalid={Boolean(erroreCampo('estremi-registrazione-input'))}
                aria-describedby={erroreCampo('estremi-registrazione-input') ? 'estremi-registrazione-input-errore' : undefined}
                className={classeErroreInput('estremi-registrazione-input')}
              />
              {erroreCampo('estremi-registrazione-input') && (
                <p id="estremi-registrazione-input-errore" role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('estremi-registrazione-input')?.messaggio}</p>
              )}
            </div>
          )}

          {/* Ritenuta: in evidenza sui passivi (com'era), sotto "Altre voci"
              sugli attivi - una SRL non subisce ritenuta sulle vendite nella
              generalita' dei casi (conferma contabile), ma il campo resta
              disponibile per l'eccezione, solo non in prima vista. */}
          {direzione === 'passivo' ? (
            <>
              <div>
                <LabelConAiuto
                  htmlFor="aliquota-ritenuta-input"
                  campo="aliquota ritenuta"
                  aiuto="Percentuale di ritenuta d'acconto sul compenso (art. 25 DPR 600/73). Lasciare vuoto se il fornitore non e' soggetto a ritenuta."
                >
                  Aliquota ritenuta % (facoltativa)
                </LabelConAiuto>
                <Input
                  id="aliquota-ritenuta-input"
                  value={aliquotaRitenuta}
                  onChange={(e) => setAliquotaRitenuta(e.target.value)}
                  placeholder="Es. 20"
                />
              </div>
              {mostraRitenuta && (
                <div>
                  <LabelConAiuto
                    htmlFor="tipo-ritenuta-input"
                    campo="tipo ritenuta"
                    aiuto="Codice tributo della ritenuta operata (es. 1040 per i redditi di lavoro autonomo)."
                  >
                    Tipo ritenuta
                  </LabelConAiuto>
                  <Input
                    id="tipo-ritenuta-input"
                    value={tipoRitenuta}
                    onChange={(e) => setTipoRitenuta(e.target.value)}
                    placeholder="Es. 1040"
                    maxLength={10}
                  />
                </div>
              )}
            </>
          ) : (
            <BloccoRichiudibile titolo="Altre voci">
              <div>
                <LabelConAiuto
                  htmlFor="aliquota-ritenuta-input"
                  campo="aliquota ritenuta"
                  aiuto="Percentuale di ritenuta d'acconto sul compenso (art. 25 DPR 600/73). La generalita' delle vendite di una SRL non ne e' soggetta: lasciare vuoto salvo conferma diversa."
                >
                  Aliquota ritenuta % (facoltativa)
                </LabelConAiuto>
                <Input
                  id="aliquota-ritenuta-input"
                  value={aliquotaRitenuta}
                  onChange={(e) => setAliquotaRitenuta(e.target.value)}
                  placeholder="Es. 20"
                />
              </div>
              {mostraRitenuta && (
                <div>
                  <LabelConAiuto
                    htmlFor="tipo-ritenuta-input"
                    campo="tipo ritenuta"
                    aiuto="Codice tributo della ritenuta operata (es. 1040 per i redditi di lavoro autonomo)."
                  >
                    Tipo ritenuta
                  </LabelConAiuto>
                  <Input
                    id="tipo-ritenuta-input"
                    value={tipoRitenuta}
                    onChange={(e) => setTipoRitenuta(e.target.value)}
                    placeholder="Es. 1040"
                    maxLength={10}
                  />
                </div>
              )}
            </BloccoRichiudibile>
          )}
        </CardContent>
      </Card>

      {/* --- Durata e rinnovo --- */}
      <Card>
        <CardHeader>
          <CardTitle>Durata e rinnovo</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className={bordoErrore('data-inizio-input')}>
            <Label htmlFor="data-inizio-input">Decorrenza</Label>
            <Input
              id="data-inizio-input"
              type="date"
              value={dataInizio}
              onChange={(e) => setDataInizio(e.target.value)}
              className={erroreCampo('data-inizio-input') ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {erroreCampo('data-inizio-input') && (
              <p role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('data-inizio-input')?.messaggio}</p>
            )}
          </div>
          <div className={bordoErrore('data-fine-input')}>
            <Label htmlFor="data-fine-input">Scadenza (facoltativa)</Label>
            <Input
              id="data-fine-input"
              type="date"
              value={dataFine}
              onChange={(e) => setDataFine(e.target.value)}
              className={erroreCampo('data-fine-input') ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {erroreCampo('data-fine-input') && (
              <p role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('data-fine-input')?.messaggio}</p>
            )}
          </div>

          <div className="md:col-span-2 flex items-center gap-2">
            <input
              id="rinnovo-check"
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={rinnovoAuto}
              onChange={(e) => setRinnovoAuto(e.target.checked)}
            />
            <label htmlFor="rinnovo-check" className="text-sm">
              Rinnovo automatico (tacito) alla scadenza
            </label>
          </div>

          {rinnovoAuto && (
            <>
              <div>
                <LabelConAiuto
                  htmlFor="preavviso-valore-input"
                  campo="preavviso"
                  aiuto="Quanto prima della scadenza va inviata la disdetta per evitare il rinnovo tacito. Locazioni commerciali: mesi (art. 28 L. 392/1978); altri contratti: tipicamente giorni."
                >
                  Preavviso
                </LabelConAiuto>
                <div className="flex items-center gap-2">
                  <Input
                    id="preavviso-valore-input"
                    type="number"
                    min="0"
                    value={preavvisoValore}
                    onChange={(e) => {
                      setPreavvisoValoreToccato(true);
                      setPreavvisoValore(e.target.value);
                    }}
                    className={`w-24 ${erroreCampo('preavviso-valore-input') ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                  />
                  {erroreCampo('preavviso-valore-input') && (
                    <p role="alert" className="text-xs text-destructive">{erroreCampo('preavviso-valore-input')?.messaggio}</p>
                  )}
                  <Selettore2
                    id="preavviso-unita"
                    ariaLabel="Unita' del preavviso"
                    value={preavvisoUnita}
                    opzioni={[
                      { value: 'giorni', label: 'giorni' },
                      { value: 'mesi', label: 'mesi' },
                    ]}
                    onChange={(v) => {
                      setPreavvisoUnitaToccata(true);
                      setPreavvisoUnita(v);
                    }}
                  />
                </div>
              </div>
              <div>
                <LabelConAiuto
                  htmlFor="rinnovo-durata-input"
                  campo="durata del rinnovo"
                  aiuto="Per quanti mesi si prolunga il contratto a ogni rinnovo tacito."
                >
                  Durata del rinnovo (mesi)
                </LabelConAiuto>
                <Input
                  id="rinnovo-durata-input"
                  type="number"
                  min="1"
                  value={rinnovoDurataMesi}
                  onChange={(e) => setRinnovoDurataMesi(e.target.value)}
                  className={`w-24 ${erroreCampo('rinnovo-durata-input') ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                />
                {erroreCampo('rinnovo-durata-input') && (
                  <p role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('rinnovo-durata-input')?.messaggio}</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* --- Ogni quanto --- */}
      <Card>
        <CardHeader>
          <CardTitle>{direzione === 'attivo' ? 'Ogni quanto si fattura' : 'Ogni quanto si paga'}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className={bordoErrore('periodicita-input')}>
            <Label htmlFor="periodicita-input">Periodicita'</Label>
            <Combobox
              id="periodicita-input"
              openOnFocus
              value={periodicitaInput}
              onInputChange={(v) => {
                setPeriodicitaInput(v);
                const match = periodicitaList.find((p) => p.descrizione === v);
                setPeriodicita(match ? match.codice : '');
              }}
              onSelect={(o) => {
                setPeriodicita(o.value);
                setPeriodicitaInput(o.label);
              }}
              options={opzioniPerLookup(periodicitaList, periodicitaInput, periodicita)}
              placeholder="Cerca la periodicita'..."
            />
            {erroreCampo('periodicita-input') && (
              <p role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('periodicita-input')?.messaggio}</p>
            )}
          </div>
          <div>
            <LabelConAiuto
              htmlFor="ancora-giorno-input"
              campo="giorno di decorrenza"
              aiuto="Il giorno del mese da cui si contano i periodi (es. 1 = dal primo del mese). Segue la decorrenza finche' non lo si cambia a mano."
            >
              Giorno di decorrenza
            </LabelConAiuto>
            <Input
              id="ancora-giorno-input"
              type="number"
              min="1"
              max="31"
              value={ancoraGiorno}
              onChange={(e) => {
                setAncoraToccata(true);
                setAncoraGiorno(e.target.value);
              }}
              className="w-24"
            />
          </div>
          {direzione === 'attivo' ? (
            <div>
              <LabelConAiuto
                htmlFor="giorni-scadenza-input"
                campo="giorni per l'incasso"
                aiuto="Quanti giorni dopo la fine del periodo scade l'incasso (0 = alla chiusura del periodo). Proposto dalla configurazione, modificabile per questo contratto."
              >
                Il cliente paga entro
              </LabelConAiuto>
              <div className="flex items-center gap-2">
                <Input
                  id="giorni-scadenza-input"
                  type="number"
                  min="0"
                  value={giorniScadenza}
                  onChange={(e) => {
                    setGiorniScadenzaToccato(true);
                    setGiorniScadenza(e.target.value);
                  }}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">giorni dalla fine del periodo</span>
              </div>
            </div>
          ) : (
            <div>
              <LabelConAiuto
                htmlFor="giorni-scadenza-input"
                campo="giorni per il pagamento"
                aiuto="Quanti giorni dopo la fine del periodo scade il pagamento (0 = alla chiusura del periodo). Proposto dalla configurazione, modificabile per questo contratto."
              >
                Giorni per il pagamento
              </LabelConAiuto>
              <Input
                id="giorni-scadenza-input"
                type="number"
                min="0"
                value={giorniScadenza}
                onChange={(e) => {
                  setGiorniScadenzaToccato(true);
                  setGiorniScadenza(e.target.value);
                }}
                className="w-24"
              />
            </div>
          )}
          {direzione === 'passivo' && (
            <div>
              <span
                id="pagamento-label"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Pagamento
              </span>
              <div>
                <Selettore2
                  id="pagamento-anticipato"
                  ariaLabelledBy="pagamento-label"
                  value={pagamentoAnticipato ? 'anticipato' : 'posticipato'}
                  opzioni={[
                    { value: 'posticipato', label: 'Posticipato (a fine periodo)' },
                    { value: 'anticipato', label: 'Anticipato (a inizio periodo)' },
                  ]}
                  onChange={(v) => setPagamentoAnticipato(v === 'anticipato')}
                />
              </div>
            </div>
          )}
          {direzione === 'attivo' && (
            <div className={bordoErrore('data-pavimento-input')}>
              <LabelConAiuto
                htmlFor="data-pavimento-input"
                campo="primo canone da noi"
                aiuto="Serve quando il contratto e' partito prima che entrasse nell'ERP: i mesi precedenti non verranno proposti."
              >
                Il primo canone che fatturiamo noi
              </LabelConAiuto>
              <Input
                id="data-pavimento-input"
                type="date"
                value={dataPavimento}
                onChange={(e) => {
                  setDataPavimentoToccato(true);
                  setDataPavimento(e.target.value);
                }}
                className={erroreCampo('data-pavimento-input') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {erroreCampo('data-pavimento-input') && (
                <p role="alert" className="mt-1 text-xs text-destructive">{erroreCampo('data-pavimento-input')?.messaggio}</p>
              )}
            </div>
          )}
          {direzione === 'attivo' && (
            <div>
              <LabelConAiuto
                htmlFor="sorgente-quantita-input"
                campo="come si conta la quantita'"
                aiuto="Fissa: lo stesso importo a ogni periodo. Se la quantita' cambia periodo per periodo, sceglierne l'origine qui."
              >
                Come si conta la quantita'
              </LabelConAiuto>
              <Combobox
                id="sorgente-quantita-input"
                openOnFocus
                value={sorgenteQuantitaInput}
                onInputChange={(v) => {
                  setSorgenteQuantitaInput(v);
                  const match = sorgenteQuantitaList.find((s) => s.descrizione === v);
                  setSorgenteQuantita(match ? match.codice : '');
                }}
                onSelect={(o) => {
                  setSorgenteQuantita(o.value);
                  setSorgenteQuantitaInput(o.label);
                }}
                options={opzioniPerLookup(sorgenteQuantitaList, sorgenteQuantitaInput, sorgenteQuantita)}
                placeholder="Fissa..."
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* --- Voci a canone --- */}
      <Card>
        <CardHeader>
          <CardTitle>Voci a canone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Descrizione</TableHead>
                <TableHead className="w-24">Quantita'</TableHead>
                <TableHead className="w-32">U.M.</TableHead>
                <TableHead className="w-32 text-right">Prezzo</TableHead>
                <TableHead className="w-24 text-right">IVA %</TableHead>
                <TableHead className="w-40">
                  <span className="inline-flex items-center gap-1">
                    Natura IVA
                    <AiutoCampo
                      campo="natura IVA"
                      testo="Da compilare SOLO per una voce esente o non imponibile (es. N4 per fabbricati abitativi): in quel caso l'aliquota IVA va a zero."
                    />
                  </span>
                </TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {righe.map((r, i) => {
                const aliquotaZeroSenzaNatura = rigaHaAliquotaZeroSenzaNatura(r);
                return (
                  <TableRow key={i}>
                    <TableCell>
                      <Input
                        id={`descrizione-riga-${i}`}
                        aria-label={`Descrizione voce ${i + 1}`}
                        aria-invalid={Boolean(erroreCampo(`descrizione-riga-${i}`))}
                        aria-describedby={erroreCampo(`descrizione-riga-${i}`) ? `descrizione-riga-${i}-errore` : undefined}
                        value={r.descrizione}
                        onChange={(e) => setRigaCampo(i, 'descrizione', e.target.value)}
                        placeholder="Descrizione della voce"
                        className={classeErroreInput(`descrizione-riga-${i}`)}
                      />
                      {erroreCampo(`descrizione-riga-${i}`) && (
                        <p id={`descrizione-riga-${i}-errore`} role="alert" className="mt-1 text-xs text-destructive">
                          {erroreCampo(`descrizione-riga-${i}`)?.messaggio}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        id={`quantita-riga-${i}`}
                        aria-label={`Quantita' voce ${i + 1}`}
                        value={r.quantita}
                        onChange={(e) => setRigaCampo(i, 'quantita', e.target.value)}
                        className="text-right tabular-nums"
                      />
                    </TableCell>
                    <TableCell>
                      <Combobox
                        id={`unita-misura-riga-${i}`}
                        ariaLabel={`Unita' di misura voce ${i + 1}`}
                        ariaInvalid={Boolean(erroreCampo(`unita-misura-riga-${i}`))}
                        ariaDescribedBy={erroreCampo(`unita-misura-riga-${i}`) ? `unita-misura-riga-${i}-errore` : undefined}
                        inputClassName={classeErroreInput(`unita-misura-riga-${i}`)}
                        openOnFocus
                        value={r.unitaMisuraInput}
                        onInputChange={(v) => cambiaUnitaMisuraInput(i, v)}
                        onSelect={(o) => selezionaUnitaMisura(i, o)}
                        options={opzioniPerLookup(unitaMisure, r.unitaMisuraInput, r.unita_misura)}
                        placeholder="U.M."
                      />
                      {erroreCampo(`unita-misura-riga-${i}`) && (
                        <p id={`unita-misura-riga-${i}-errore`} role="alert" className="mt-1 text-xs text-destructive">
                          {erroreCampo(`unita-misura-riga-${i}`)?.messaggio}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        id={`prezzo-riga-${i}`}
                        aria-label={`Prezzo unitario voce ${i + 1}`}
                        aria-invalid={Boolean(erroreCampo(`prezzo-riga-${i}`))}
                        aria-describedby={erroreCampo(`prezzo-riga-${i}`) ? `prezzo-riga-${i}-errore` : undefined}
                        value={r.prezzo_unitario}
                        onChange={(e) => setRigaCampo(i, 'prezzo_unitario', e.target.value)}
                        className={`${classeErroreInput(`prezzo-riga-${i}`)} text-right tabular-nums`}
                        placeholder="0.00"
                      />
                      {erroreCampo(`prezzo-riga-${i}`) && (
                        <p id={`prezzo-riga-${i}-errore`} role="alert" className="mt-1 text-xs text-destructive">
                          {erroreCampo(`prezzo-riga-${i}`)?.messaggio}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        id={`aliquota-riga-${i}`}
                        aria-label={`Aliquota IVA voce ${i + 1}`}
                        aria-invalid={Boolean(erroreCampo(`aliquota-riga-${i}`))}
                        aria-describedby={erroreCampo(`aliquota-riga-${i}`) ? `aliquota-riga-${i}-errore` : undefined}
                        value={r.aliquota_iva_percentuale}
                        onChange={(e) => cambiaAliquotaRiga(i, e.target.value)}
                        className={`${classeErroreInput(`aliquota-riga-${i}`)} text-right tabular-nums`}
                      />
                      {erroreCampo(`aliquota-riga-${i}`) && (
                        <p id={`aliquota-riga-${i}-errore`} role="alert" className="mt-1 text-xs text-destructive">
                          {erroreCampo(`aliquota-riga-${i}`)?.messaggio}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Combobox
                        id={`natura-iva-riga-${i}`}
                        ariaLabel={`Natura IVA voce ${i + 1}`}
                        ariaInvalid={Boolean(erroreCampo(`natura-iva-riga-${i}`))}
                        ariaDescribedBy={erroreCampo(`natura-iva-riga-${i}`) ? `natura-iva-riga-${i}-errore` : undefined}
                        inputClassName={classeErroreInput(`natura-iva-riga-${i}`)}
                        openOnFocus
                        value={r.naturaIvaInput}
                        onInputChange={(v) => cambiaNaturaIvaInput(i, v)}
                        onSelect={(o) => selezionaNaturaRiga(i, o)}
                        options={opzioniPerLookup(natureIva, r.naturaIvaInput, r.natura_iva_codice)}
                        placeholder="Se esente..."
                      />
                      {aliquotaZeroSenzaNatura && (
                        <p id={`natura-iva-riga-${i}-errore`} role="alert" className="mt-1 text-xs text-destructive">
                          Aliquota zero richiede una natura IVA (controllo SDI 00400).
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={righe.length === 1}
                        onClick={() => rimuoviRiga(i)}
                        aria-label={`Rimuovi voce ${i + 1}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Button type="button" variant="ghost" size="sm" onClick={aggiungiRiga}>
            <Plus className="h-4 w-4" />
            Aggiungi voce
          </Button>
        </CardContent>
      </Card>

      {/* --- Anteprima --- */}
      <Card>
        <CardHeader>
          <CardTitle>{direzione === 'attivo' ? 'Cosa fattureremo' : 'Anteprima delle scadenze'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={() => void calcolaAnteprima()}
              disabled={calcolando}
              aria-describedby={motivoCalcolaVisibile ? 'motivo-calcola-spento' : undefined}
            >
              {calcolando ? 'Calcolo...' : 'Calcola anteprima'}
            </Button>
            {motivoCalcolaVisibile && (
              <span id="motivo-calcola-spento" className="text-sm text-muted-foreground">
                Manca: {mancantiCalcolo.join(', ')}.
              </span>
            )}
            {erroreAnteprima && (
              <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {erroreAnteprima}
              </div>
            )}
          </div>

          {/* I3: fuori dal blocco condizionale sull'anteprima, visibile appena
              l'orizzonte e' noto (non solo dopo aver calcolato). */}
          {orizzonteAnno != null && (
            <p className="text-xs text-muted-foreground">
              Le scadenze si possono aprire fino al {orizzonteAnno}.
            </p>
          )}

          {anteprima && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
                <span>
                  <strong>{anteprima.length}</strong> period{anteprima.length === 1 ? 'o' : 'i'}, totale{' '}
                  <strong className="tabular-nums">{fmtEuro(totalePrevisto)}</strong> - stima a canone costante
                  {mostraRitenuta && nettoPrevisto != null && (
                    <>
                      {' '}
                      (netto dopo ritenuta <strong className="tabular-nums">{fmtEuro(nettoPrevisto)}</strong>)
                    </>
                  )}
                </span>
              </div>

              {direzione === 'attivo' && (
                <p className="text-sm text-muted-foreground">
                  Alla creazione, <strong>{fatturabiliSubito}</strong>{' '}
                  {fatturabiliSubito === 1 ? "canone risultera'" : 'canoni risulteranno'} subito fatturabil
                  {fatturabiliSubito === 1 ? 'e' : 'i'}.
                </p>
              )}
              {direzione === 'attivo' && fatturabiliSubito > 3 && (
                <Alert variant="warning">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Attenzione: {fatturabiliSubito} canoni risulterebbero gia' scaduti alla creazione.
                    Sopra 3 la fatturazione automatica si ferma finche' non li agganci o non li emetti
                    uno alla volta.
                  </AlertDescription>
                </Alert>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Periodo</TableHead>
                    <TableHead>{direzione === 'attivo' ? "Quando si potra' fatturare" : 'Scadenza'}</TableHead>
                    <TableHead className="text-right">Imponibile</TableHead>
                    <TableHead className="text-right">IVA</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    {mostraRitenuta && <TableHead className="text-right">Ritenuta</TableHead>}
                    {mostraRitenuta && <TableHead className="text-right">Netto</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {anteprima.map((p, idx) => {
                    const fatturabileDal = direzione === 'attivo' ? giornoDopo(p.periodo_fine) : null;
                    const giaFatturabile =
                      fatturabileDal != null && !sottoIlPavimento(p.periodo_fine) && fatturabileDal <= oggi;
                    return (
                      <TableRow key={idx}>
                        <TableCell className="whitespace-nowrap">
                          {fmtDate(p.periodo_inizio)} - {fmtDate(p.periodo_fine)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {direzione === 'attivo' ? (
                            <span className="inline-flex items-center gap-2">
                              {fmtDate(fatturabileDal)}
                              {giaFatturabile && <Badge variant="success">Pronto da fatturare</Badge>}
                            </span>
                          ) : (
                            fmtDate(p.data_scadenza)
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtEuro(p.imponibile)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtEuro(p.iva)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtEuro(p.totale)}</TableCell>
                        {mostraRitenuta && (
                          <TableCell className="text-right tabular-nums">{fmtEuro(p.ritenuta)}</TableCell>
                        )}
                        {mostraRitenuta && (
                          <TableCell className="text-right tabular-nums">{fmtEuro(p.netto_a_pagare)}</TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {scadenzaOltreOrizzonte && orizzonteAnno != null && (
                <Alert variant="warning">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Attenzione: alcune scadenze cadono dopo il 31/12/{orizzonteAnno}. La creazione
                    verra' rifiutata finche' non si aggiungono le partizioni mancanti.
                  </AlertDescription>
                </Alert>
              )}

              {!dataFine && (
                <p className="text-xs text-muted-foreground">
                  {direzione === 'attivo'
                    ? "Contratto senza scadenza: qui vedi i primi 12 mesi. Il contratto prosegue finche' non lo disdici, e ogni canone viene proposto al termine del suo periodo."
                    : "Contratto senza scadenza: le scadenze si aprono solo per i primi 12 mesi; oltre questa finestra andranno aperte a parte."}
                </p>
              )}
            </>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
            <TooltipRoot>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => void crea()}
                  disabled={salvando}
                  {...(motivoCreaSpenta() ? { 'aria-describedby': 'motivo-crea-spento' } : {})}
                >
                  {salvando ? 'Creazione...' : 'Crea il contratto e apri le scadenze'}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Crea il contratto e apre subito le sue scadenze nello scadenzario.
              </TooltipContent>
            </TooltipRoot>
            {motivoCreaSpenta() && (
              <span id="motivo-crea-spento" className="text-sm text-muted-foreground">
                {motivoCreaSpenta()}
              </span>
            )}
            <Button type="button" variant="ghost" onClick={chiediUscita}>
              Annulla
            </Button>
            {erroreCreazione && (
              <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {erroreCreazione}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={confermaUscitaAperta} onOpenChange={setConfermaUscitaAperta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abbandonare il nuovo contratto?</DialogTitle>
            <DialogDescription>
              Il form contiene dati non salvati: uscendo si perdono e il contratto non viene creato.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfermaUscitaAperta(false)}>
              Resta
            </Button>
            <Button variant="destructive" onClick={() => navigate(-1)}>
              Esci senza salvare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContrattoNuovo;
